/** Canonical URL root segment for the Exam Management product. */
export const EXAM_MANAGEMENT_ROOT = "exam-management"

/** Build a path under `/exam-management/…` without hardcoding the product root. */
export function examPath(...segments: string[]): string {
  const tail = segments.filter(Boolean).join("/")
  return tail ? `/${EXAM_MANAGEMENT_ROOT}/${tail}` : `/${EXAM_MANAGEMENT_ROOT}`
}

/** Student exam dashboard — primary hub for the Student persona. */
export const EXAM_MANAGEMENT_MY_EXAMS_PATH = examPath("my-exams")

export function studentMyExamsDashboardPath(
  category?: "upcoming" | "concluded",
): string {
  if (!category) return EXAM_MANAGEMENT_MY_EXAMS_PATH
  return `${EXAM_MANAGEMENT_MY_EXAMS_PATH}?category=${category}`
}

/** Student released scores — transcript-style reading hub. */
export const EXAM_MANAGEMENT_RESULTS_PATH = examPath("results")

/** Per-exam results view — owned by My Exams, not Exam analytics. */
export function studentExamResultsPath(assessmentId: string): string {
  return examPath("my-exams", assessmentId, "results")
}

/** @deprecated Use studentExamResultsPath. */
export function studentResultReviewPath(assessmentId: string): string {
  return studentExamResultsPath(assessmentId)
}

/** Released + pending result links from hubs and table actions. */
export function studentResultPath(assessmentId: string): string {
  return studentExamResultsPath(assessmentId)
}

/** Default admin/faculty landing when opening Exam Management. */
export const EXAM_MANAGEMENT_ADMIN_HOME_PATH = examPath(
  "course-offerings",
  "c1",
)

export type AssessmentPathScope = {
  assessmentId: string
  /** When set, nest under the course offering so Course Offerings stays active. */
  courseId?: string | null
  /** Assessments hub: keep the flat `/assessments/:id` URL. */
  fromAssessmentsHub?: boolean
}

/**
 * Assessment detail (and child) URLs.
 * Course work uses `/course-offerings/:courseId/assessments/:id` (Option A).
 * The Assessments hub keeps `/assessments/:id`.
 */
export function assessmentPath(
  scope: AssessmentPathScope,
  ...suffix: string[]
): string {
  const id = scope.assessmentId
  if (!scope.fromAssessmentsHub && scope.courseId) {
    return examPath(
      "course-offerings",
      scope.courseId,
      "assessments",
      id,
      ...suffix,
    )
  }
  return examPath("assessments", id, ...suffix)
}

/** Child links inherit nest vs hub from the current route params. */
export function assessmentPathFromParams(
  params: { assessmentId?: string; courseId?: string },
  ...suffix: string[]
): string {
  return assessmentPath(
    {
      assessmentId: params.assessmentId ?? "",
      courseId: params.courseId,
      fromAssessmentsHub: !params.courseId,
    },
    ...suffix,
  )
}
